import { Placement } from '@iheartjane/dm-sdk';
import type {
  BrandSpecials,
  FetchCustomPlacementsProps,
  MultiPlacementResponse,
  SmartSort,
  Specials,
} from '@iheartjane/dm-sdk';
import { useCallback, useEffect, useMemo, useState } from 'react';

import type { AlgoliaProduct } from '@jane/search/types';
import { useSDK } from '@jane/shared-ecomm/providers';

import type { UseFetchPlacementProps } from './useFetchPlacement';

type SmartMultiPlacementResponseViewModel = {
  fetchNextPage: () => Promise<void>;
  hasNextPage: boolean;
  isPlacement: true;
  placement: SmartSort<AlgoliaProduct>;
};

type NonSmartMultiPlacementResponseViewModel = {
  fetchNextPage: () => Promise<void>;
  hasNextPage: boolean;
  isPlacement: false;
  placement: BrandSpecials | Specials | undefined;
};

type MultiPlacementResponseViewModel =
  | SmartMultiPlacementResponseViewModel
  | NonSmartMultiPlacementResponseViewModel;

interface UseFetchCustomMultiPlacementsResponse {
  isError: boolean;
  isLoading: boolean;
  isSuccess: boolean;
  placementViewModels: MultiPlacementResponseViewModel[];
}

type UseFetchCustomMultiPlacementsProps = Omit<
  UseFetchPlacementProps<FetchCustomPlacementsProps>,
  'dependencies' | 'searchAttributes'
>;

function isSmartSortPlacement(
  placement: MultiPlacementResponse<AlgoliaProduct>
): placement is SmartSort<AlgoliaProduct> {
  return (
    placement.placement !== Placement.BrandSpecials &&
    placement.placement !== Placement.Specials
  );
}

export const useFetchCustomMultiPlacements = <
  ProductShape extends AlgoliaProduct,
>(
  props: UseFetchCustomMultiPlacementsProps
): UseFetchCustomMultiPlacementsResponse => {
  const {
    enabled,
    numColumns,
    storeId,
    placements: requestedPlacements,
  } = props;

  const [isLoading, setIsLoading] = useState(false);
  const [isError, setIsError] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [placements, setPlacements] = useState<
    MultiPlacementResponse<AlgoliaProduct>[] | undefined
  >(undefined);

  const { sdk } = useSDK();

  const stringifiedRequestedPlacements = JSON.stringify(requestedPlacements);

  const fetchMultiPlacements = useCallback(async () => {
    if (!sdk) {
      return;
    }
    setIsLoading(true);
    setIsError(false);
    setIsSuccess(false);
    setPlacements(undefined);

    try {
      const response = enabled
        ? await sdk.fetchCustomPlacements<ProductShape>({
            numColumns,
            searchAttributes: ['*'],
            storeId,
            placements: requestedPlacements,
          })
        : undefined;
      setPlacements(response);
      setIsSuccess(true);
    } catch {
      setIsError(true);
    } finally {
      setIsLoading(false);
    }
  }, [enabled, sdk, storeId, stringifiedRequestedPlacements]);

  useEffect(() => {
    fetchMultiPlacements();
  }, [fetchMultiPlacements]);

  const placementViewModels = useMemo(() => {
    if (!placements) {
      return [
        {
          fetchNextPage: async () => {},
          hasNextPage: false,
          isPlacement: false as const,
          placement: undefined,
        },
      ];
    }

    return placements.map<MultiPlacementResponseViewModel>((placement) => {
      if (isSmartSortPlacement(placement)) {
        return {
          fetchNextPage: async () => {
            if (placement.hasNextPage()) {
              setIsLoading(true);
              setIsError(false);
              setIsSuccess(false);
              try {
                await placement.nextPage();
                setIsSuccess(true);
              } catch {
                setIsError(true);
              } finally {
                setIsLoading(false);
              }
            }
          },
          hasNextPage: placement.hasNextPage(),
          isPlacement: true as const,
          placement,
        };
      }

      return {
        fetchNextPage: async () => {},
        hasNextPage: false,
        isPlacement: false as const,
        placement,
      };
    });
  }, [placements]);

  return {
    placementViewModels,
    isError,
    isLoading,
    isSuccess,
  };
};
